import Fuse from "fuse.js";

import payersData from "@/src/data/payers.json";
import { Coverage } from "@/src/models/coverage";

const primaryPayerIdToShortId = new Map<string, string>(
  (payersData as { shortId: string; primaryPayerId?: string | null }[])
    .filter((p) => p.primaryPayerId)
    .map((p) => [p.primaryPayerId!, p.shortId])
);

export interface EcwInsuranceMappingEntry {
  ecwId: string;
  ecwPlanName: string;
  ecwPayerId: string;
  isDefault: boolean;
  primaryEligible: boolean;
}

export type EcwInsuranceMapping = Record<string, EcwInsuranceMappingEntry[]>;

const PLAN_MATCH_FUSE_THRESHOLD = 0.4;

export function normalizePlanForMatching(plan: string): string {
  return plan
    .trim()
    .toLowerCase()
    .replace(/[\s\-_/]+/g, " ")
    .trim();
}

export function findBestMatchingEcwPlan(
  planFromReferral: string,
  eligible: EcwInsuranceMappingEntry[]
): EcwInsuranceMappingEntry | null {
  const normalizedQuery = normalizePlanForMatching(planFromReferral);

  if (!normalizedQuery) return null;

  const fuse = new Fuse(eligible, {
    keys: ["ecwPlanName"],
    threshold: PLAN_MATCH_FUSE_THRESHOLD,
    ignoreLocation: true,
    includeScore: true,
    isCaseSensitive: false,
  });

  const results = fuse.search(normalizedQuery, { limit: 1 });
  const best = results[0];

  if (!best || (best.score ?? 1) > PLAN_MATCH_FUSE_THRESHOLD) return null;

  return best.item;
}

export function resolveCoverageEcwId(
  coverage: Coverage | null,
  mapping: EcwInsuranceMapping,
  forPrimary: boolean
): string | null {
  if (!coverage?.customerPayer) return null;
  const payerKey = primaryPayerIdToShortId.get(coverage.customerPayer) ?? coverage.customerPayer;
  const entries = mapping[payerKey];

  if (!entries?.length) return null;
  const eligible = forPrimary ? entries.filter((e) => e.primaryEligible) : entries;

  if (eligible.length === 0) return null;
  const planFromReferral = coverage.plan?.trim() ?? "";
  const match = planFromReferral ? findBestMatchingEcwPlan(planFromReferral, eligible) : null;

  if (match) return match.ecwId;
  const defaultEntry = eligible.find((e) => e.isDefault);

  return defaultEntry?.ecwId ?? eligible[0]?.ecwId ?? null;
}

export type PatientMetadataInsuranceVariables = {
  primary_ecw_plan_id: string;
  primary_group_number: string;
  primary_subscriber_number: string;
  secondary_ecw_plan_id: string;
  secondary_group_number: string;
  secondary_subscriber_number: string;
};

export function buildPatientMetadataInsuranceVariables(
  primary: Coverage | null,
  secondary: Coverage | null,
  primaryEcwId: string | null,
  secondaryEcwId: string | null
): PatientMetadataInsuranceVariables {
  return {
    primary_ecw_plan_id: primaryEcwId ?? "",
    primary_group_number: primary?.groupNumber?.trim() ?? "",
    primary_subscriber_number: primary?.subscriberId?.trim() ?? "",
    secondary_ecw_plan_id: secondaryEcwId ?? "",
    secondary_group_number: secondary?.groupNumber?.trim() ?? "",
    secondary_subscriber_number: secondary?.subscriberId?.trim() ?? "",
  };
}
